import * as React from 'react';
import { useMemo, useState } from 'react';
import { Archive, Edit, Mail, PowerOff, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { RequirePermission } from '@klareospace/pharos-lib-front-utils';
import { USER_STATUS, type SortDirection, type UserWithResolvedScopes } from '@klareospace/pharos-lib-types';
import { getFullName } from '@klareospace/pharos-lib-utils';
import type {
  DataTableColumn,
  DataTablePagination,
  DataTableSorting,
  MultiSelectOption,
  RowAction,
  SimplePagination,
} from '@klareospace/pharos-lib-web-utils';
import {
  createActionsColumn,
  DataTable,
  RowActionsMenu,
  UserStatusBadge,
  VehicleProfileIcon,
} from '@klareospace/pharos-lib-web-utils';

import { PersonIcon } from '&/shared/components';

import { UserDrawer } from '../../users/components/user-drawer';
import { UserScopeCell } from '../../users/components/user-scope-cell';
import { useArchiveDriver, useDeactivateDriver, useActivateDriver, useResendDriverInvitation } from '../hooks';
import type { DriverAction, DriverSortableColumn, DriversSorting } from '../types/driver.types';
import { DRIVER_ACTIONS } from '../types/driver.types';
import { DriverConfirmDialog } from './driver-confirm-dialog';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DriversTableProps {
  carrierOptions?: MultiSelectOption[];
  drivers: UserWithResolvedScopes[];
  isLoading: boolean;
  pagination: SimplePagination;
  sorting: DriversSorting;
}

interface ConfirmAction {
  driver: UserWithResolvedScopes;
  type: DriverAction;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DriversTable({
  carrierOptions,
  drivers,
  isLoading,
  pagination,
  sorting,
}: DriversTableProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('settings');

  const archiveDriver = useArchiveDriver();
  const deactivateDriver = useDeactivateDriver();
  const activateDriver = useActivateDriver();
  const { mutate: resendInvitation } = useResendDriverInvitation();

  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [editingDriver, setEditingDriver] = useState<UserWithResolvedScopes | null>(null);

  const getRowActions = useMemo(
    () =>
      (driver: UserWithResolvedScopes): RowAction[] => {
        const actions: RowAction[] = [
          {
            icon: <Edit size={16} />,
            label: tCommon('actions.edit'),
            onClick: (): void => setEditingDriver(driver),
          },
        ];

        if (driver.status === USER_STATUS.PENDING) {
          actions.push({
            icon: <Mail size={16} />,
            label: tCommon('actions.resendInvitation'),
            onClick: (): void => resendInvitation(driver.email),
          });
        }

        if (driver.status === USER_STATUS.ACTIVE) {
          actions.push({
            icon: <PowerOff size={16} />,
            label: tCommon('actions.deactivate'),
            onClick: (): void => setConfirmAction({ driver, type: DRIVER_ACTIONS.DEACTIVATE }),
          });
        }

        if (driver.status === USER_STATUS.INACTIVE) {
          actions.push({
            icon: <RefreshCw size={16} />,
            label: tCommon('actions.activate'),
            onClick: (): void => setConfirmAction({ driver, type: DRIVER_ACTIONS.ACTIVATE }),
          });
        }

        if (driver.deletedAt === null || driver.deletedAt === undefined) {
          actions.push({
            className: 'text-red-600',
            hasSeparator: true,
            icon: <Archive size={16} />,
            label: tCommon('actions.archive'),
            onClick: (): void => setConfirmAction({ driver, type: DRIVER_ACTIONS.ARCHIVE }),
          });
        }

        return actions;
      },
    [resendInvitation, t],
  );

  const columns = useMemo<DataTableColumn<UserWithResolvedScopes>[]>(() => {
    const baseColumns: DataTableColumn<UserWithResolvedScopes>[] = [
      {
        cell: (driver): React.ReactNode => (
          <div className="flex items-center gap-3">
            <PersonIcon name={getFullName(driver.firstName, driver.lastName)} />
            <div className="min-w-0">
              <button
                className="cursor-pointer truncate text-sm font-medium text-start hover:underline"
                onClick={(): void => setEditingDriver(driver)}
                type="button"
              >
                {getFullName(driver.firstName, driver.lastName)}
              </button>
              <p className="truncate text-xs text-muted-foreground">{driver.email}</p>
            </div>
          </div>
        ),
        header: tCommon('fields.driverColumnLabel'),
        key: 'driver',
      },
      {
        cell: (driver): React.ReactNode => (
          <UserScopeCell resolvedScopes={driver.resolvedScopes} roles={driver.roles} scopes={driver.scopes} />
        ),
        className: 'hidden md:table-cell',
        header: tCommon('fields.scope'),
        key: 'scope',
      },
    ];

    if (carrierOptions !== undefined) {
      baseColumns.push({
        cell: (driver): React.ReactNode => {
          const carrierId = driver.driverAttributes?.carrierId;
          const carrier = carrierOptions.find((o) => o.value === carrierId);
          return carrier ? (
            <span className="text-sm">{carrier.label}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        },
        className: 'hidden md:table-cell',
        header: tCommon('fields.carrier'),
        key: 'carrier',
      });
    }

    baseColumns.push(
      {
        cell: (driver): React.ReactNode => (
          <UserStatusBadge label={tCommon(`statuses.${driver.status}`)} status={driver.status} />
        ),
        header: tCommon('fields.status'),
        key: 'status',
      },
      {
        cell: (driver): React.ReactNode => {
          const vehicle = driver.resolvedDefaultVehicle;
          if (!vehicle) {
            return <span className="text-xs text-muted-foreground">{tCommon('statuses.unassigned')}</span>;
          }
          return (
            <div className="flex items-center gap-2">
              <VehicleProfileIcon
                className="text-muted-foreground"
                profile={vehicle.vehicleTemplate.profile}
                size={16}
              />
              <span className="font-mono text-sm">{vehicle.licensePlate}</span>
            </div>
          );
        },
        className: 'hidden md:table-cell',
        header: t('common.defaultVehicle'),
        key: 'defaultVehicle',
      },
      createActionsColumn<UserWithResolvedScopes>((driver) => (
        <RequirePermission permission="drivers:manage">
          <RowActionsMenu actions={getRowActions(driver)} ariaLabel={tCommon('table.actionsHeader')} />
        </RequirePermission>
      )),
    );

    return baseColumns;
  }, [carrierOptions, getRowActions, t]);

  const tablePagination = useMemo<DataTablePagination>(
    () => ({
      currentPage: pagination.currentPage,
      nextLabel: tCommon('pagination.nextPage'),
      onPageChange: pagination.onPageChange,
      pageLabel: tCommon('pagination.pageLabel', { current: pagination.currentPage, total: pagination.totalPages }),
      pageSize: pagination.pageSize,
      previousLabel: tCommon('pagination.previousPage'),
      showingText: (startItem: number, endItem: number, totalItems: number): string =>
        t('drivers.table.showing', { from: startItem, to: endItem, total: totalItems }),
      totalItems: pagination.totalItems,
      totalPages: pagination.totalPages,
    }),
    [pagination, t, tCommon],
  );

  const tableSorting = useMemo<DataTableSorting>(
    () => ({
      onSort: (field: string, direction: SortDirection | null): void =>
        sorting.onSort(field as DriverSortableColumn, direction),
      sortBy: sorting.sortBy,
      sortDirection: sorting.sortDirection,
    }),
    [sorting],
  );

  function handleConfirm(): void {
    if (!confirmAction) return;
    if (confirmAction.type === DRIVER_ACTIONS.ARCHIVE) {
      archiveDriver.mutate(confirmAction.driver.id);
    } else if (confirmAction.type === DRIVER_ACTIONS.DEACTIVATE) {
      deactivateDriver.mutate(confirmAction.driver.id);
    } else {
      activateDriver.mutate(confirmAction.driver.id);
    }
    setConfirmAction(null);
  }

  function handleDialogOpenChange(open: boolean): void {
    if (!open) setConfirmAction(null);
  }

  return (
    <>
      <DataTable<UserWithResolvedScopes>
        ariaLabel={t('drivers.table.ariaLabel')}
        columns={columns}
        emptyMessage={t('drivers.table.empty')}
        isLoading={isLoading}
        pagination={tablePagination}
        rowKey={(driver): string => driver.id}
        rows={drivers}
        sorting={tableSorting}
      />
      <DriverConfirmDialog
        confirmAction={confirmAction}
        onConfirm={handleConfirm}
        onOpenChange={handleDialogOpenChange}
      />
      <UserDrawer
        entryPoint="drivers"
        onClose={(): void => setEditingDriver(null)}
        open={editingDriver !== null}
        user={editingDriver}
      />
    </>
  );
}
